/**
 * Tour state is stored in localStorage, keyed per user.
 *
 * Shape:
 *   {
 *     status: 'pending' | 'in_progress' | 'completed' | 'skipped_once' | 'skipped_twice',
 *     current_step: 0 | 1 | 2 | 3,
 *     skip_count: 0 | 1 | 2,
 *     // Snapshot of counts at tour start, so we detect NEW additions, not
 *     // pre-existing items (important for existing users who already have
 *     // transactions / rules / wishlist items before the tour ever shows).
 *     baseline: {
 *       rules: number,
 *       transactions: number,
 *       no_spend_days: number,
 *       wishlist: number,
 *     }
 *   }
 *
 * Steps:
 *   0 = Home / no-spend rule
 *   1 = Budget / transaction
 *   2 = Calendar / no-spend day
 *   3 = Wishlist / wishlist item
 */

const keyFor = (userId) => `worthly_tour_state_${userId}`;

const DEFAULT_STATE = {
  status: 'pending',
  current_step: 0,
  skip_count: 0,
  baseline: null,
};

export const getTourState = (userId) => {
  if (!userId) return DEFAULT_STATE;
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_STATE, ...parsed };
  } catch (e) {
    return DEFAULT_STATE;
  }
};

export const setTourState = (userId, state) => {
  if (!userId) return;
  try {
    localStorage.setItem(keyFor(userId), JSON.stringify(state));
  } catch (e) {
    // localStorage quota errors shouldn't crash the app
  }
};

export const updateTourState = (userId, patch) => {
  const current = getTourState(userId);
  const next = { ...current, ...patch };
  setTourState(userId, next);
  return next;
};

/**
 * Should we show the tour right now?
 * - pending: yes
 * - in_progress: yes (user refreshed mid-tour)
 * - skipped_once, skipped_twice, completed: no
 *   (Per Pass 11: if the user skips once, we don't ask again.)
 */
export const shouldShowTour = (userId) => {
  const state = getTourState(userId);
  return (
    state.status === 'pending' ||
    state.status === 'in_progress'
  );
};

export const markTourStarted = (userId, baseline) => {
  updateTourState(userId, {
    status: 'in_progress',
    current_step: 0,
    baseline,
  });
};

export const markStepCompleted = (userId, stepIndex) => {
  // Advance current_step to the next step (cap at 3, don't overflow).
  const next = Math.min(stepIndex + 1, 4);
  updateTourState(userId, { current_step: next });
};

export const markTourCompleted = (userId) => {
  updateTourState(userId, {
    status: 'completed',
    current_step: 4,
  });
};

export const markTourSkipped = (userId) => {
  const current = getTourState(userId);
  const nextSkipCount = current.skip_count + 1;
  updateTourState(userId, {
    status: nextSkipCount >= 2 ? 'skipped_twice' : 'skipped_once',
    skip_count: nextSkipCount,
  });
};

// Some Worthly collections are arrays (rules, transactions, wishlist_items)
// and some are objects keyed by date (no_spend_days). Normalize to a count.
const countOf = (v) => {
  if (!v) return 0;
  if (Array.isArray(v)) return v.length;
  if (typeof v === 'object') return Object.keys(v).length;
  return 0;
};

/**
 * Compute baseline counts at tour start.
 * Called from TourController with current userData collections.
 *
 * calendarDays combines no_spend_days + spend_days + neutral_days because
 * the step advances on ANY calendar log, not just no-spend.
 */
export const computeBaseline = ({ rules, transactions, noSpendDays, spendDays, neutralDays, wishlistItems }) => ({
  rules: countOf(rules),
  transactions: countOf(transactions),
  calendarDays: countOf(noSpendDays) + countOf(spendDays) + countOf(neutralDays),
  wishlist: countOf(wishlistItems),
});

export { countOf };
